import { AxialRotationEnum, BoneTypeEnum, Consts, FormatUtils, PlanTypeEnum, SelectedApexMech, StateTypes, ViewType } from "@ortho-next/nextray-core";
import { Vector3 } from "three";
import { PlateOrientationType } from "../Models/AppModel";
import { BindedModel, bindedModel } from "../Models/BindedModel";
import { BoneTypeMinEnum } from "../Models/Enums";
import { PlateModel } from "../Models/PlateModel";
import { Context } from "../States/Context";
import { CustomStandardAngles, OsteotomyCut, RegisteringType } from "../States/State";
import { OsteotomyAP } from "../Tools/Osteotomy/OsteotomyAP";
import { MeasuresUtils } from "../Utils/MeasuresUtils";
import { Main } from "./Main";

/**
 * Handle events logic.
 */
export class ContextInit {
	private static _previousPlateState = false;
	private static _previousPlateOrientation: PlateOrientationType;
	private static _previousPlateModel: PlateModel;
	private static _previousBoneTypeLT: BoneTypeEnum | null;
	private static _previousMechAxisLT: Vector3[] | null; //todo move in class instance or do a specific class
	private static _previousMechAxisLTValid: boolean | null;

	/**
	 * Init all events logic.
	 */
	public static create(main: Main, context: Context): Context {

		context.addEventListener("stopRegistering", () => {
			main.model.magnifier = undefined;
			// restore previous bone type LT
			if (this._previousBoneTypeLT) { //todo move it
				bindedModel.boneTypeLT = this._previousBoneTypeLT;
				main.toolsLT.mechanicalAxis && (main.toolsLT.mechanicalAxis.boneType = bindedModel.boneTypeLT);
				main.toolsLT.mechanicalAxis && (main.toolsLT.mechanicalAxis.isValid = this._previousMechAxisLTValid!);
				bindedModel.isMechanicalAxisLTValid = this._previousMechAxisLTValid!;
				main.toolsLT.clonedMechanicalAxis && (main.toolsLT.clonedMechanicalAxis.boneType = bindedModel.boneTypeLT);
				this._previousBoneTypeLT = null;
				this._previousMechAxisLT = null;
				this._previousMechAxisLTValid = null;
			}
		});

		context.addEventListener("completeRegistering", (event) => {
			switch (event.args) {
				case RegisteringType.mechanicalAxisAP:
					main.model.isAPMechanicalAxisInserted = true;
					break;
				case RegisteringType.mechanicalAxisLT:
					main.model.isLTMechanicalAxisInserted = true;
					this._previousBoneTypeLT = null;
					this._previousMechAxisLT = null;
					break;
				case RegisteringType.contralateral:
					main.model.isContralateralAxisInserted = true;
					break;
			}
		});

		context.addEventListener("generateImagesToPrint", () => {
			main.model.printResult = main.printUtils.generateImages(main.model.stateType);
		});

		context.addEventListener("updateMeausuresOnlyPrint", () => {
			if (main.hasAP) {
				const mecAxis = main.toolsAP.mechanicalAxis;
				const osteotomy = main.toolsAP.osteotomy as OsteotomyAP;
				const EOCplane = main.toolsAP.EoCPlane!;
				if (main.model.stateType === StateTypes.EOC || main.model.stateType === StateTypes.templating) {
					main.model.print_osteotomyLevel = FormatUtils.distance(MeasuresUtils.getOsteotomyLevel(mecAxis, osteotomy, main.model.selectedApex as SelectedApexMech)!);
				}
				if (main.model.stateType === StateTypes.templating) {
					const plate = main.toolsAP.plate;
					if (plate && main.model.plate) {
						const plateAngle = main.model.plate.plate.angle!;
						main.model.print_cortexWire = FormatUtils.distance(MeasuresUtils.getCortexWire(mecAxis, osteotomy, main.model.selectedApex as SelectedApexMech, plate.wireOrigin!)!);
						main.model.print_wireAngle = FormatUtils.angleRadiants(MeasuresUtils.getWireInsertionAngle(mecAxis, EOCplane, plateAngle, main.model.selectedApex as SelectedApexMech)!);
					}
				}
			}
		});

		context.addEventListener("insertMechanicalAxis", (event) => {
			main.toolsInit.initMechanicalAxis(event.args);

			if (!main.getTools(event.args).mechanicalAxis.isValid) {
				event.args === ViewType.LT && (main.toolsLT.mechanicalAxis.boneType = bindedModel.boneTypeLT!);
				main.setView(event.args);
				if (event.args === ViewType.LT && this._previousBoneTypeLT) {
					this.registerMechanicalAxis(main, context, event.args, { oldState: this._previousMechAxisLT!, oldType: this._previousBoneTypeLT });
				} else {
					this.registerMechanicalAxis(main, context, event.args);
				}
				main.model.magnifier = this.generateMagnifierImage(main);
			}
		});

		context.addEventListener("insertControlaterMechanicalAxis", () => {
			main.toolsInit.initControlaterMechanicalAxis();
			if (!main.toolsAP.contralateralMechanicalAxis.isValid) {
				main.setView(ViewType.AP);
				this.registerControlaterMechanicalAxis(main, context);
				main.model.magnifier = this.generateMagnifierImage(main);
			}
		});

		context.addEventListener("startBoneTypeSelection", (event) => {
			main.setView(ViewType.LT);
			bindedModel.insertingMechanicalAxisLT = true;
		});

		context.addEventListener("cancelBoneTypeSelection", (event) => {
			bindedModel.insertingMechanicalAxisLT = false;
		});

		context.addEventListener("confirmBoneTypeSelection", (event) => {
			if (event.args !== bindedModel.boneTypeLT) {
				this._previousBoneTypeLT = bindedModel.boneTypeLT;
				main.toolsLT.mechanicalAxis && (this._previousMechAxisLT = main.toolsLT.mechanicalAxis.getPoints());
				main.toolsLT.mechanicalAxis && (this._previousMechAxisLTValid = main.toolsLT.mechanicalAxis.isValid);
				bindedModel.boneTypeLT = event.args;
				main.toolsLT.mechanicalAxis && (main.toolsLT.mechanicalAxis.boneType = bindedModel.boneTypeLT);
				main.toolsLT.clonedMechanicalAxis && (main.toolsLT.clonedMechanicalAxis.boneType = bindedModel.boneTypeLT);

				main.toolsLT.mechanicalAxis && (main.toolsLT.mechanicalAxis.isValid = false);
				bindedModel.isMechanicalAxisLTValid = false;
			}
			bindedModel.insertingMechanicalAxisLT = false;
		});

		// TODO: unire select apex con confirm apex
		context.addEventListener("selectApex", (event) => {
			main.model.selectedApex = event.args;
		});

		context.addEventListener("confirmApex", () => {
			main.toolsInit.initReferencePoints();
			main.toolsInit.initOsteotomies();
			main.toolsInit.osteotomiesManager.setPosition(main.model.selectedApex);
			bindedModel.isOsteotomyValid = true;
		});

		context.addEventListener("insertPlate", (event) => {
			main.model.plate = event.args;
			main.toolsInit.initPlates();
			bindedModel.isPlateInserted = true;
			main.model.isPlateInserted = true;
			bindedModel.layer_plate = true;
		});

		context.addEventListener("startInsertingPlate", () => {
			this._previousPlateState = main.model.isPlateInserted;
			this._previousPlateOrientation = main.model.plateOrientation;
			this._previousPlateModel = main.model.plate!;
			main.toolsAP.plate?.createStateToRestore();
			main.toolsLT.plate?.createStateToRestore();
		});

		context.addEventListener("cancelPlate", () => {
			main.toolsAP.plate?.confirmStateRestore();
			main.toolsLT.plate?.confirmStateRestore();
			bindedModel.isPlateInserted = this._previousPlateState;
			main.model.isPlateInserted = this._previousPlateState;
			main.model.plateOrientation = this._previousPlateOrientation;
			main.model.plate = this._previousPlateModel;
		});

		context.addEventListener("confirmPlate", () => {
			main.toolsAP.plate?.cancelStateRestore();
			main.toolsLT.plate?.cancelStateRestore();
			main.history.clear();
		});

		context.addEventListener("flipPlate", (event) => {
			main.model.plateOrientation = event.args;
			main.toolsAP.plate?.horizontalFlip();
			main.toolsLT.plate?.horizontalFlip();
		});

		context.addEventListener("updateScrews", (event) => {
			main.model.plate!.screwList = event.args;
			main.model.plate!.hasJPSExtended = true;
			main.toolsAP.plate?.changeScrewList(event.args);
			main.toolsLT.plate?.changeScrewList(event.args);
		});

		context.addEventListener("setStandardAngle", (event) => {
			switch (event.args.angle) {
				case CustomStandardAngles.LDTA:
					main.history.startTracking(ViewType.AP, main.toolsAP.mechanicalAxis);
					main.model.def_standardAngles_LDTA = event.args.value;
					main.toolsAP.mechanicalAxis.tibia.LDTAStandardDegrees = event.args.value;
					main.history.detectChanges(ViewType.AP);
					return;
				case CustomStandardAngles.LPFA:
					main.history.startTracking(ViewType.AP, main.toolsAP.mechanicalAxis);
					main.model.def_standardAngles_LPFA = event.args.value;
					main.toolsAP.mechanicalAxis.femur.LPFAStandardDegrees = event.args.value;
					main.history.detectChanges(ViewType.AP);
					return;
				case CustomStandardAngles.mLDFA:
					main.history.startTracking(ViewType.AP, main.toolsAP.mechanicalAxis);
					main.model.def_standardAngles_mLDFA = event.args.value;
					main.toolsAP.mechanicalAxis.femur.mLDFAStandardDegrees = event.args.value;
					main.history.detectChanges(ViewType.AP);
					return;
				case CustomStandardAngles.MPTA:
					main.history.startTracking(ViewType.AP, main.toolsAP.mechanicalAxis);
					main.model.def_standardAngles_MPTA = event.args.value;
					main.toolsAP.mechanicalAxis.tibia.MPTAStandardDegrees = event.args.value;
					main.history.detectChanges(ViewType.AP);
					return;
				case CustomStandardAngles.PPTA:
					main.history.startTracking(ViewType.LT, main.toolsLT.mechanicalAxis);
					main.model.def_standardAngles_PPTA = event.args.value;
					main.toolsLT.mechanicalAxis.tibia!.PPTAStandardDegrees = event.args.value;
					main.history.detectChanges(ViewType.LT);
					return;
				case CustomStandardAngles.ADTA:
					main.history.startTracking(ViewType.LT, main.toolsLT.mechanicalAxis);
					main.model.def_standardAngles_ADTA = event.args.value;
					main.toolsLT.mechanicalAxis.tibia!.ADTAStandardDegrees = event.args.value;
					main.history.detectChanges(ViewType.LT);
					return;
			}
		});

		context.addEventListener("setAxialRotation", (event) => {
			if (event.args.boneType == BoneTypeMinEnum.Femur) {
				main.model.def_femur_axialRotation = event.args.value;
			} else {
				main.model.def_tibia_axialRotation = event.args.value;
			}
		});

		context.addEventListener("setAxialRotationSign", (event) => {
			if (event.args.boneType === BoneTypeMinEnum.Femur) {
				main.model.def_femur_axialRotationSign = event.args.value;
			} else {
				main.model.def_tibia_axialRotationSign = event.args.value;
			}
		});

		context.addEventListener("osteotomyCut", (event) => {
			main.model.osteotomyCut = event.args;
			bindedModel.cutType = event.args;
		});

		context.addEventListener("cutEoC", (event) => {
			if (event.args) {
				let APValid = true, LTValid = true;
				const verticalTransl = MeasuresUtils.getEoCVerticalTranslationBeforeCut(main.toolsAP, main.toolsLT, main.model.osteotomyCut)
				main.toolsInit.initEoCPlanes();
				if (main.hasAP) {
					const cutPoint = main.toolsAP.mechanicalAxis.cutPoint
					APValid = main.toolsAP.EoCPlane!.cutPlane(cutPoint, verticalTransl!, main.model.osteotomyCut);
				}
				if (main.hasLT && APValid) {
					const cutPoint = main.toolsLT.mechanicalAxis.cutPoint
					LTValid = main.toolsLT.EoCPlane!.cutPlane(cutPoint, verticalTransl!, main.model.osteotomyCut);
				}
				if (APValid && LTValid) {
					// move this in EoCPlaneManager
					bindedModel.EOCCropVisible = true;
					main.model.eocCut = true;
					main.toolsAP.EoCPlane?.dispatchEvent({ type: 'updated' });
					main.toolsLT.EoCPlane?.dispatchEvent({ type: 'updated' });
					main.history.clear();
				} else {
					main.bridge.dispatchEvent({ type: "onResult", args: 'cannotCut' });
				}
			} else {
				main.model.eocCut = false;
				bindedModel.EOCCropVisible = false;
				main.history.clear();
			}
		});

		context.addEventListener("switchToCalibrated", (event) => {
			this.reset(StateTypes.calibrated, main);
		});

		context.addEventListener("switchToDeformity", (event) => {
			this.reset(StateTypes.deformityAnalysis, main);
		});

		context.addEventListener("switchToEOC", (event) => {
			if (main.isPostOp && main.caseModel.type === PlanTypeEnum.Fracture) {
				main.toolsInit.initReferencePoints();
				main.toolsInit.initOsteotomies();
				main.toolsInit.osteotomiesManager.setPosition(SelectedApexMech.none);
			}

			this.reset(StateTypes.EOC, main);
		});

		context.addEventListener("switchToTemplating", (event) => {
			this.reset(StateTypes.templating, main);
		});

		context.addEventListener("rotateCamera", () => {
			main.rotateCameraByState();
		});

		return context;
	}

	private static generateMagnifierImage(main: Main): string {
		main.multiviewRenderer.render();
		return main.renderer.domElement.toDataURL();
	}

	private static registerMechanicalAxis(main: Main, context: Context, viewType: ViewType, oldMechAxisLT?: { oldState: Vector3[], oldType: BoneTypeEnum }): void {
		const tools = main.getTools(viewType);
		const insertingMechanicalAxis: keyof BindedModel = viewType === ViewType.AP ? "insertingMechanicalAxisAP" : "insertingMechanicalAxisLT";
		const isMechanicalAxisValid: keyof BindedModel = viewType === ViewType.AP ? "isMechanicalAxisAPValid" : "isMechanicalAxisLTValid";
		const oldValidState = bindedModel[isMechanicalAxisValid];
		const oldStateTool = tools.mechanicalAxis.getPoints();

		bindedModel[insertingMechanicalAxis] = true;
		main.activeCameraControl.fitToPlane();
		main.activeCameraControl.enabled = false;

		tools.registerHitPlane!.startRegisteringForObj(tools.mechanicalAxis, main.activeCamera, {
			onRegError: () => {
				main.bridge.dispatchEvent({ type: "onResult", args: 'mechanicalAxisInsertError' })
			},
			onRegEnd: (args) => {
				bindedModel[insertingMechanicalAxis] = false;
				bindedModel[isMechanicalAxisValid] = args || oldValidState;
				tools.mechanicalAxis.isValid = bindedModel[isMechanicalAxisValid]; //todo refactor
				main.activeCameraControl.enabled = true;
				main.history.clear(viewType);
				context.handle("endRegistering", null);
			},
			onRegSuccess: () => {
				bindedModel.layer_mechAxis_mechanical = true;
				bindedModel.layer_mechAxis_anatomical = true;
				bindedModel.layer_mechAxis_weightBearing = true;
			},
			onRegCancel: () => {
				if (oldMechAxisLT) {
					tools.mechanicalAxis.boneType = oldMechAxisLT.oldType;
					oldMechAxisLT.oldState && tools.mechanicalAxis.restorePoints(oldMechAxisLT.oldState);
				} else {
					tools.mechanicalAxis.restorePoints(oldStateTool);
				}
			},
			onRegReset: () => {
				bindedModel[insertingMechanicalAxis] = true;
				main.activeCameraControl.fitToPlane();
				main.activeCameraControl.enabled = false;
			}
		}, true);
	}

	private static registerControlaterMechanicalAxis(main: Main, context: Context): void {
		const tools = main.toolsAP;
		const oldValidState = bindedModel.isControlateralMechanicalAxisValid;
		const oldStateTool = tools.contralateralMechanicalAxis.getPoints();

		bindedModel.insertingControlateralMechanicalAxis = true;
		main.activeCameraControl.fitToPlane();
		main.activeCameraControl.enabled = false;

		tools.registerHitPlane!.startRegisteringForObj(tools.contralateralMechanicalAxis, main.activeCamera, {
			onRegError: () => {
				main.bridge.dispatchEvent({ type: "onResult", args: 'contralateralAxisInsertError' })
			},
			onRegEnd: (args) => {
				bindedModel['insertingControlateralMechanicalAxis'] = false;
				bindedModel['isControlateralMechanicalAxisValid'] = args || oldValidState;
				tools.contralateralMechanicalAxis.isValid = args || oldValidState; //todo refactor
				main.activeCameraControl.enabled = true;
				context.handle("endRegistering", null);
			},
			onRegSuccess: () => {
				bindedModel.layer_contralateral_all = true;
			},
			onRegCancel: () => {
				tools.contralateralMechanicalAxis.restorePoints(oldStateTool);
			},
			onRegReset: () => {
				bindedModel.insertingControlateralMechanicalAxis = true;
				main.activeCameraControl.fitToPlane();
				main.activeCameraControl.enabled = false;
			}
		}, true);
	}

	private static reset(landingState: StateTypes, main: Main): void {
		main.history.clear();

		if (landingState <= StateTypes.calibrated) {
			this.resetDeformity(main);
			this.resetLines(main);
			this.resetGoniometers(main);
		}

		if (landingState <= StateTypes.deformityAnalysis) {
			this.resetEOC(main);
		}

		if (landingState <= StateTypes.EOC) {
			this.resetTemplating(main);
		}
	}

	private static resetDeformity(main: Main): void {
		// Mechanical analysis
		bindedModel.isMechanicalAxisAPValid = false;
		main.toolsAP.mechanicalAxis && (main.toolsAP.mechanicalAxis.isValid = false);
		bindedModel.isMechanicalAxisLTValid = false;
		main.toolsLT.mechanicalAxis && (main.toolsLT.mechanicalAxis.isValid = false);
		bindedModel.isControlateralMechanicalAxisValid = false;
		main.toolsAP.contralateralMechanicalAxis && (main.toolsAP.contralateralMechanicalAxis.isValid = false);
		main.model.isAPMechanicalAxisInserted = false;
		main.model.isLTMechanicalAxisInserted = false;
		main.model.isContralateralAxisInserted = false;

		main.model.def_femur_axialRotation = 0;
		main.model.def_femur_axialRotationSign = AxialRotationEnum.external;
		main.model.def_tibia_axialRotation = 0;
		main.model.def_tibia_axialRotationSign = AxialRotationEnum.external;
		main.model.def_standardAngles_LPFA = Consts.paleyAngles.AP.mLPFA.standard;
		main.model.def_standardAngles_mLDFA = Consts.paleyAngles.AP.mLDFA.standard;
		main.model.def_standardAngles_MPTA = Consts.paleyAngles.AP.MPTA.standard;
		main.model.def_standardAngles_LDTA = Consts.paleyAngles.AP.LDTA.standard;
		main.model.def_standardAngles_PPTA = Consts.paleyAngles.LT.PPTA.standard;
		main.model.def_standardAngles_ADTA = Consts.paleyAngles.LT.ADTA.standard;

		bindedModel.boneTypeLT = main.caseModel.boneType;
		main.toolsLT.mechanicalAxis && (main.toolsLT.mechanicalAxis.boneType = bindedModel.boneTypeLT);

		if (main.isPostOp) {
			bindedModel.isOsteotomyValid = false;
		}
	}

	private static resetLines(main: Main): void {
		for (const line of [...main.toolsAP.lines, ...main.toolsLT.lines]) {
			line.dispose();
		}
		main.toolsAP.lines = [];
		main.toolsLT.lines = [];
		bindedModel.linesMaskLT = 0;
		bindedModel.linesMaskAP = 0;
	}

	private static resetGoniometers(main: Main): void {
		for (const goniometer of [...main.toolsAP.goniometers, ...main.toolsLT.goniometers]) {
			goniometer.dispose();
		}
		main.toolsAP.goniometers = [];
		main.toolsLT.goniometers = [];
		bindedModel.goniometersMaskLT = 0;
		bindedModel.goniometersMaskAP = 0;
	}

	private static resetEOC(main: Main): void {
		main.model.selectedApex = SelectedApexMech.none;
		if (!main.isPostOp) {
			bindedModel.isOsteotomyValid = false;
		}
		bindedModel.ostTooFarAP = false;
		bindedModel.ostTooFarLT = false;
		main.model.osteotomyCut = OsteotomyCut.none;
		main.model.eocCut = false;
		bindedModel.EOCCropVisible = false;

		main.toolsLT.clonedMechanicalAxis && (main.toolsLT.clonedMechanicalAxis.boneType = bindedModel.boneTypeLT!);
	}

	private static resetTemplating(main: Main): void {
		main.hasAP && main.toolsAP.plateManager?.reset();
		main.hasLT && main.toolsLT.plateManager?.reset();
		main.model.plate = undefined;
		main.model.plateOrientation = PlateOrientationType.lateral;
		main.model.isPlateInserted = false;
		bindedModel.isPlateInserted = false;
	}
}
